import React from 'react';
import { Box, Chip, Paper, Stack, Typography } from '@mui/material';
import { FixedSizeList as VirtualList } from 'react-window';

const GroupCard = ({ group, onSelect }) => {
  const statuses = Object.entries(group.statuses || {});

  return (
    <Paper
      elevation={0}
      onClick={() => onSelect && onSelect(group)}
      sx={{
        p: 1.2,
        borderRadius: 2,
        border: '1px solid #e5e7eb',
        cursor: 'pointer',
        transition: 'all .2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 16px rgba(15,23,42,0.08)'
        }
      }}
    >
      <Stack direction='row' justifyContent='space-between' alignItems='center' gap={1}>
        <Typography sx={{ fontWeight: 800 }} noWrap title={group.key}>{group.key}</Typography>
        <Chip size='small' label={`${group.count} pedidos`} color='primary' />
      </Stack>

      <Stack direction='row' gap={0.8} flexWrap='wrap' mt={1}>
        <Chip size='small' variant='outlined' label={`Unidades ${group.total_quantity}`} />
        <Chip size='small' variant='outlined' label={`SKUs ${(group.skus || []).length}`} />
        <Chip size='small' variant='outlined' label={`Clientes ${(group.clients || []).length}`} />
      </Stack>

      <Stack direction='row' gap={0.6} flexWrap='wrap' mt={1}>
        {statuses.length === 0 && <Chip size='small' label='Sin estados' variant='outlined' />}
        {statuses.slice(0, 5).map(([status, quantity]) => (
          <Chip key={`${group.key}-${status}`} size='small' label={`${status}: ${quantity}`} />
        ))}
      </Stack>

      {(group.subgroups || []).length > 0 && (
        <Typography variant='caption' sx={{ mt: 1, display: 'block', color: 'primary.main', fontWeight: 700 }}>
          {group.subgroups.length} subgrupos disponibles
        </Typography>
      )}
    </Paper>
  );
};

const GroupCards = ({ groups = [], onSelect, virtualizationThreshold = 80 }) => {
  if (!groups.length) {
    return (
      <Paper elevation={0} sx={{ p: 2.4, borderRadius: 2, border: '1px dashed #d4d4d8', textAlign: 'center' }}>
        <Typography sx={{ color: 'text.secondary' }}>No hay grupos para mostrar.</Typography>
      </Paper>
    );
  }

  if (groups.length > virtualizationThreshold) {
    const rowHeight = 170;
    return (
      <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid #e5e7eb', p: 1 }}>
        <Typography variant='caption' sx={{ color: 'text.secondary', mb: 0.5, display: 'block' }}>
          Vista virtualizada activa por volumen alto ({groups.length} grupos)
        </Typography>
        <VirtualList
          height={Math.min(680, groups.length * rowHeight)}
          width='100%'
          itemCount={groups.length}
          itemSize={rowHeight}
        >
          {({ index, style }) => (
            <Box style={style} sx={{ px: 0.5, py: 0.7 }}>
              <GroupCard group={groups[index]} onSelect={onSelect} />
            </Box>
          )}
        </VirtualList>
      </Paper>
    );
  }

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(3, minmax(0, 1fr))' }, gap: 1 }}>
      {groups.map((group) => (
        <GroupCard key={`${group.key}-${group.count}-${group.total_quantity}`} group={group} onSelect={onSelect} />
      ))}
    </Box>
  );
};

export default GroupCards;
